// src/components/RouterAgent.js
// Agent router — designed around the real backend pipeline
// (ai_service.agent_router / agent_router_service.get_router_instructions):
//   courriel entrant → instructions actives couvrant la marque/boîte ?
//   (sinon AUCUN routage) → spam ? → sujet de routage → agent IA (ou
//   redirection).
// Tabs (?tab= in the URL): routing (instructions coverage + sujets),
// classification (independent ticket-topic taxonomy), journal (stats + logs).
// Brand-specific instructions win over the global "Toutes les marques" scope,
// which acts as fallback — the coverage list encodes exactly that.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaPlus, FaPen, FaTrash, FaTimes, FaCheck, FaCheckCircle, FaTimesCircle,
  FaExclamationTriangle, FaSync, FaDownload, FaBan, FaShareSquare,
  FaExternalLinkAlt, FaSpinner, FaEnvelope, FaChevronDown, FaChevronRight,
  FaProjectDiagram, FaCommentDots, FaListAlt
} from 'react-icons/fa';
import './RouterAgent.css';

const TABS = ['routing', 'classification', 'journal'];

const RouterAgent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'routing';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'routing') next.delete('tab'); else next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // ── Data ──
  const [topics, setTopics] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [brands, setBrands] = useState([]);
  const [outlookAccounts, setOutlookAccounts] = useState([]);
  const [topicsConfig, setTopicsConfig] = useState(null);
  const [topicsConfigSaving, setTopicsConfigSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsDays, setStatsDays] = useState(30);
  const [journalBrand, setJournalBrand] = useState('');
  const [logs, setLogs] = useState(null);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const logsLimit = 20;
  const [logFilters, setLogFilters] = useState({ topic: '', is_spam: '', source: '' });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogIds, setShowLogIds] = useState(false);

  // ── Modals ──
  const [topicModal, setTopicModal] = useState(null); // {editing, form:{name,description}, error, saving}
  const [instrModal, setInstrModal] = useState(null); // {editing, form, redirectInput, error, saving}
  const [customTopicForm, setCustomTopicForm] = useState({ name: '', description: '' });
  const [deletingIds, setDeletingIds] = useState([]);

  // ── Feedback ──
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);
  const showFeedback = useCallback((type, text) => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ type, text });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 5000);
  }, []);
  useEffect(() => () => clearTimeout(feedbackTimer.current), []);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  // ══════════ Fetchers ══════════

  const fetchTopics = useCallback(async () => {
    try {
      const res = await axios.get('/api/tickets/user-topics', { headers: authHeaders() });
      setTopics(res.data.topics || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setTopics([]);
      showFeedback('error', t('routerAgent.fetchError', 'Impossible de charger les sujets de routage.'));
    }
  }, [showFeedback, t]);

  const fetchInstructions = useCallback(async () => {
    try {
      const res = await axios.get('/api/agent-router/instructions', { headers: authHeaders() });
      setInstructions(res.data || []);
    } catch (err) {
      console.error('Error fetching instructions:', err);
      setInstructions([]);
      showFeedback('error', t('routerAgent.instr.fetchError', 'Impossible de charger les instructions.'));
    }
  }, [showFeedback, t]);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({ days: statsDays });
      if (journalBrand) params.append('brand_id', journalBrand);
      const res = await axios.get(`/api/agent-router/logs/stats?${params.toString()}`, { headers: authHeaders() });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setStats(null);
    }
  }, [statsDays, journalBrand]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: logsPage, limit: logsLimit });
      if (journalBrand) params.append('brand_id', journalBrand);
      if (logFilters.topic) params.append('topic', logFilters.topic);
      if (logFilters.is_spam !== '') params.append('is_spam', logFilters.is_spam);
      if (logFilters.source) params.append('source', logFilters.source);
      const res = await axios.get(`/api/agent-router/logs?${params.toString()}`, { headers: authHeaders() });
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs([]);
    }
  }, [logsPage, journalBrand, logFilters]);

  const fetchTopicsConfig = useCallback(async () => {
    try {
      const res = await axios.get('/api/logistics/topics-config', { headers: authHeaders() });
      setTopicsConfig(res.data);
    } catch (err) {
      console.error('Error fetching topics config:', err);
      showFeedback('error', t('routerAgent.classif.fetchError', 'Impossible de charger la configuration de classification.'));
    }
  }, [showFeedback, t]);

  useEffect(() => {
    const headers = authHeaders();
    axios.get('/api/brands/list', { headers })
      .then((res) => setBrands(res.data || []))
      .catch((err) => console.error('Error fetching brands:', err));
    axios.get('/api/social/outlook/accounts', { headers })
      .then((res) => setOutlookAccounts(res.data.accounts || []))
      .catch((err) => console.error('Error fetching Outlook accounts:', err));
    fetchTopics();
    fetchInstructions();
  }, [fetchTopics, fetchInstructions]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'journal') fetchLogs();
  }, [activeTab, fetchLogs]);

  useEffect(() => {
    if (activeTab === 'classification' && topicsConfig === null) fetchTopicsConfig();
  }, [activeTab, topicsConfig, fetchTopicsConfig]);

  // ══════════ Helpers ══════════

  const brandId = (b) => b.id || b._id;
  const getBrandName = (id) => {
    if (!id) return t('routerAgent.allBrands', 'Toutes les marques');
    const brand = brands.find((b) => String(brandId(b)) === String(id));
    return brand?.name || id;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString(i18n.language || 'fr', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const periodLabel = {
    7: t('routerAgent.logs.last7', '7 derniers jours'),
    30: t('routerAgent.logs.last30', '30 derniers jours'),
    90: t('routerAgent.logs.last90', '90 derniers jours'),
    365: t('routerAgent.logs.lastYear', '12 derniers mois'),
  }[statsDays];

  // ══════════ Topics CRUD ══════════

  const openTopicModal = (topic = null) => {
    setTopicModal({
      editing: topic,
      form: { name: topic?.name || '', description: topic?.description || '' },
      error: null,
      saving: false,
    });
  };

  const saveTopic = async () => {
    const { form, editing } = topicModal;
    if (!form.name.trim()) {
      setTopicModal((m) => ({ ...m, error: t('routerAgent.nameRequired', 'Le nom du sujet est requis.') }));
      return;
    }
    setTopicModal((m) => ({ ...m, saving: true, error: null }));
    try {
      if (editing) {
        await axios.put(`/api/tickets/user-topics/${editing._id}`, form, { headers: authHeaders() });
        showFeedback('success', t('routerAgent.topicUpdated', 'Sujet mis à jour.'));
      } else {
        await axios.post('/api/tickets/user-topics', form, { headers: authHeaders() });
        showFeedback('success', t('routerAgent.topicCreated', 'Sujet créé.'));
      }
      setTopicModal(null);
      fetchTopics();
    } catch (err) {
      setTopicModal((m) => ({
        ...m, saving: false,
        error: err.response?.data?.detail || t('routerAgent.saveError', 'Échec de l’enregistrement du sujet.'),
      }));
    }
  };

  const deleteTopic = async (topic) => {
    if (!window.confirm(t('routerAgent.deleteTopicConfirm', 'Supprimer ce sujet de routage ? Le routeur ne pourra plus l’assigner.'))) return;
    setDeletingIds((prev) => [...prev, topic._id]);
    try {
      await axios.delete(`/api/tickets/user-topics/${topic._id}`, { headers: authHeaders() });
      showFeedback('success', t('routerAgent.topicDeleted', 'Sujet supprimé.'));
      fetchTopics();
    } catch (err) {
      showFeedback('error', t('routerAgent.deleteError', 'Échec de la suppression du sujet.'));
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== topic._id));
    }
  };

  // ══════════ Instructions CRUD ══════════

  const openInstrModal = (instr = null, presetBrandId = '') => {
    setInstrModal({
      editing: instr,
      form: {
        brand_id: instr ? (instr.brand_id || '') : presetBrandId,
        is_active: instr ? instr.is_active : true,
        instructions: instr?.instructions || '',
        apply_to_emails: instr?.apply_to_emails || [],
        redirect_emails: instr?.redirect_emails || [],
      },
      redirectInput: '',
      error: null,
      saving: false,
    });
  };

  const saveInstruction = async () => {
    const { form, editing } = instrModal;
    if (!form.instructions.trim()) {
      setInstrModal((m) => ({ ...m, error: t('routerAgent.instr.instructionsRequired', 'Les instructions sont requises.') }));
      return;
    }
    setInstrModal((m) => ({ ...m, saving: true, error: null }));
    const payload = {
      is_active: form.is_active,
      instructions: form.instructions,
      apply_to_emails: form.apply_to_emails.length > 0 ? form.apply_to_emails : null,
      redirect_emails: form.redirect_emails.length > 0 ? form.redirect_emails : null,
    };
    try {
      if (editing) {
        await axios.put(`/api/agent-router/instructions/${editing.id}`, payload, { headers: authHeaders() });
        showFeedback('success', t('routerAgent.instr.updated', 'Instructions mises à jour.'));
      } else {
        await axios.post('/api/agent-router/instructions', { ...payload, brand_id: form.brand_id || null }, { headers: authHeaders() });
        showFeedback('success', t('routerAgent.instr.created', 'Instructions créées — le routage est actif pour cette portée.'));
      }
      setInstrModal(null);
      fetchInstructions();
    } catch (err) {
      setInstrModal((m) => ({
        ...m, saving: false,
        error: err.response?.data?.detail || t('routerAgent.instr.saveError', 'Échec de l’enregistrement des instructions.'),
      }));
    }
  };

  const deleteInstruction = async (instr) => {
    if (!window.confirm(t('routerAgent.instr.deleteConfirm', 'Supprimer ces instructions ? Les tickets de cette portée ne seront plus routés (sauf portée globale).'))) return;
    setDeletingIds((prev) => [...prev, instr.id]);
    try {
      await axios.delete(`/api/agent-router/instructions/${instr.id}`, { headers: authHeaders() });
      showFeedback('success', t('routerAgent.instr.deleted', 'Instructions supprimées.'));
      fetchInstructions();
    } catch (err) {
      showFeedback('error', t('routerAgent.instr.deleteError', 'Échec de la suppression des instructions.'));
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== instr.id));
    }
  };

  const toggleInstruction = async (instr) => {
    try {
      await axios.put(`/api/agent-router/instructions/${instr.id}`, { is_active: !instr.is_active }, { headers: authHeaders() });
      fetchInstructions();
    } catch (err) {
      showFeedback('error', t('routerAgent.instr.toggleError', 'Échec de l’activation/désactivation.'));
    }
  };

  // ══════════ Classification ══════════

  const saveTopicsConfig = async (nextConfig) => {
    setTopicsConfigSaving(true);
    try {
      const payload = {
        classification_enabled: nextConfig.classification_enabled,
        disabled_topics: nextConfig.disabled_topics || [],
        custom_topics: (nextConfig.custom_topics || []).map((c) => ({
          slug: c.slug || null, name: c.name, description: c.description || '',
        })),
      };
      const res = await axios.put('/api/logistics/topics-config', payload, { headers: authHeaders() });
      setTopicsConfig(res.data);
      showFeedback('success', t('routerAgent.classif.saved', 'Configuration de classification enregistrée.'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      showFeedback('error', typeof detail === 'string' ? detail : t('routerAgent.classif.saveError', 'Échec de l’enregistrement de la classification.'));
    } finally {
      setTopicsConfigSaving(false);
    }
  };

  // ══════════ CSV export ══════════

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (journalBrand) params.append('brand_id', journalBrand);
      if (logFilters.topic) params.append('topic', logFilters.topic);
      if (logFilters.is_spam !== '') params.append('is_spam', logFilters.is_spam);
      if (logFilters.source) params.append('source', logFilters.source);
      const res = await axios.get(`/api/agent-router/logs/export?${params.toString()}`, {
        headers: authHeaders(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'agent_router_logs.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting logs:', err);
      showFeedback('error', t('routerAgent.logs.exportError', 'Échec de l’export CSV.'));
    }
  };

  // ══════════ Derived ══════════

  const globalActive = (instructions || []).some((i) => !i.brand_id && i.is_active);
  const topicVolumes = stats?.topic_breakdown || null;
  const maxTopicVolume = topicVolumes ? Math.max(1, ...Object.values(topicVolumes)) : 1;

  const decisionOf = (log) => {
    if (log.is_spam) return { key: 'spam', label: t('routerAgent.logs.spam', 'Spam') };
    if (log.redirected_to) return { key: 'redirect', label: t('routerAgent.logs.redirected', 'Redirigé') };
    return { key: 'routed', label: t('routerAgent.logs.routed', 'Routé') };
  };

  const topicFilterOptions = [...new Set([
    ...(topics || []).map((tp) => tp.name),
    ...(logs || []).map((l) => l.topic).filter(Boolean),
  ])];

  const totalLogsPages = Math.max(1, Math.ceil(logsTotal / logsLimit));
  const getPageItems = () => {
    const anchors = new Set([1, totalLogsPages, logsPage - 1, logsPage, logsPage + 1]);
    const pages = [...anchors].filter((p) => p >= 1 && p <= totalLogsPages).sort((a, b) => a - b);
    const items = [];
    let prev = 0;
    pages.forEach((p) => {
      if (p - prev > 1) items.push(`gap-${p}`);
      items.push(p);
      prev = p;
    });
    return items;
  };

  const skeleton = (rows = 4) => (
    <div className="ra-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => <div key={i} className="ra-skeleton-row" />)}
    </div>
  );

  // ══════════ Render: coverage row ══════════

  const renderScopeRow = (scopeBrandId, scopeName) => {
    const own = (instructions || []).filter((i) => String(i.brand_id || '') === String(scopeBrandId || ''));
    const isGlobalScope = !scopeBrandId;

    let stateEl;
    if (own.length === 0) {
      if (!isGlobalScope && globalActive) {
        stateEl = (
          <span className="ra-cov-state covered">
            {t('routerAgent.cov.coveredByGlobal', 'Couvert par « Toutes les marques »')}
          </span>
        );
      } else {
        stateEl = (
          <span className="ra-cov-state gap">
            <FaExclamationTriangle /> {t('routerAgent.cov.noInstructions', 'Aucune instruction — tickets non routés')}
          </span>
        );
      }
    }

    return (
      <li key={scopeBrandId || 'global'} className="ra-cov-row">
        <div className="ra-cov-scope">
          <span className="ra-cov-name">{scopeName}</span>
          {isGlobalScope && (
            <span className="ra-cov-fallback">{t('routerAgent.cov.fallback', 'portée par défaut')}</span>
          )}
        </div>
        <div className="ra-cov-body">
          {own.length === 0 ? (
            <div className="ra-cov-line">
              {stateEl}
              <button type="button" className="ra-cov-add" onClick={() => openInstrModal(null, scopeBrandId || '')}>
                <FaPlus /> {t('routerAgent.cov.add', 'Ajouter')}
              </button>
            </div>
          ) : own.map((instr) => (
            <div key={instr.id} className="ra-cov-line">
              <span className={`ra-cov-state ${instr.is_active ? 'active' : 'inactive'}`}>
                {instr.is_active
                  ? (<><FaCheck /> {t('routerAgent.cov.active', 'Instructions actives')}</>)
                  : (<><FaBan /> {t('routerAgent.cov.inactive', 'Désactivées — tickets non routés')}</>)}
              </span>
              {(instr.apply_to_emails || []).length > 0 && (
                <span className="ra-cov-chips" title={(instr.apply_to_emails || []).join(', ')}>
                  <FaEnvelope />
                  {instr.apply_to_emails.length === 1
                    ? instr.apply_to_emails[0]
                    : t('routerAgent.cov.mailboxes', '{{total}} boîtes mail', { total: instr.apply_to_emails.length })}
                </span>
              )}
              {(instr.redirect_emails || []).length > 0 && (
                <span className="ra-cov-chips" title={(instr.redirect_emails || []).join(', ')}>
                  <FaShareSquare />
                  {t('routerAgent.cov.redirects', '{{total}} redirections', { total: instr.redirect_emails.length })}
                </span>
              )}
              <div className="ra-cov-actions">
                <button
                  type="button"
                  role="switch"
                  aria-checked={instr.is_active}
                  className={`ra-switch ${instr.is_active ? 'on' : ''}`}
                  onClick={() => toggleInstruction(instr)}
                  title={instr.is_active ? t('routerAgent.instr.deactivate', 'Désactiver') : t('routerAgent.instr.activate', 'Activer')}
                >
                  <span className="ra-switch-knob" />
                </button>
                <button type="button" className="ra-icon-btn" onClick={() => openInstrModal(instr)} title={t('routerAgent.instr.edit', 'Modifier')} aria-label={t('routerAgent.instr.edit', 'Modifier')}>
                  <FaPen />
                </button>
                <button
                  type="button"
                  className="ra-icon-btn danger"
                  onClick={() => deleteInstruction(instr)}
                  disabled={deletingIds.includes(instr.id)}
                  title={t('common.delete', 'Supprimer')}
                  aria-label={t('common.delete', 'Supprimer')}
                >
                  {deletingIds.includes(instr.id) ? <FaSpinner className="ra-spin" /> : <FaTrash />}
                </button>
              </div>
            </div>
          ))}
        </div>
      </li>
    );
  };

  // ══════════ Render: tabs ══════════

  const renderRoutingTab = () => (
    <div className="ra-stack">
      {/* The gate: instructions coverage */}
      <div className="ra-card">
        <div className="ra-card-header">
          <div className="ra-card-heading">
            <h2>{t('routerAgent.cov.title', 'Instructions & activation')}</h2>
            <p>{t('routerAgent.cov.subtitle', 'Le routeur ne traite un courriel que si des instructions actives couvrent sa marque ou sa boîte mail — sans instructions, aucun routage.')}</p>
          </div>
          <button type="button" className="u-btn u-btn--primary" onClick={() => openInstrModal()}>
            <FaPlus /> {t('routerAgent.instr.create', 'Créer des instructions')}
          </button>
        </div>
        {instructions === null ? skeleton(3) : (
          <ul className="ra-cov-list">
            {renderScopeRow(null, t('routerAgent.allBrands', 'Toutes les marques'))}
            {brands.map((b) => renderScopeRow(brandId(b), b.name))}
          </ul>
        )}
      </div>

      {/* The vocabulary: routing subjects */}
      <div className="ra-card">
        <div className="ra-card-header">
          <div className="ra-card-heading">
            <h2>
              {t('routerAgent.topicsTitle', 'Sujets de routage')}
              {topics !== null && <span className="ra-card-count">{topics.length}</span>}
            </h2>
            <p>{t('routerAgent.topicsSubtitle', 'Le routeur choisit le sujet d’après sa description ; les sujets alimentent aussi les onglets du tableau de bord.')}</p>
          </div>
          <button type="button" className="u-btn u-btn--primary" onClick={() => openTopicModal()}>
            <FaPlus /> {t('routerAgent.createTopic', 'Créer un sujet')}
          </button>
        </div>
        {topics === null ? skeleton(3) : topics.length === 0 ? (
          <div className="ra-empty">
            <FaProjectDiagram className="ra-empty-icon" />
            <p>{t('routerAgent.noTopicsDesc', 'Aucun sujet pour le moment. Créez vos segments (ex. : Avant vente, Après vente) pour que le routeur classe les tickets.')}</p>
          </div>
        ) : (
          <ul className="ra-topic-list">
            {topics.map((topic) => {
              const volume = topicVolumes ? topicVolumes[topic.name] : null;
              return (
                <li key={topic._id} className="ra-topic-row">
                  <div className="ra-topic-main">
                    <span className="ra-topic-name">{topic.name}</span>
                    {topic.description ? (
                      <span className="ra-topic-desc" title={topic.description}>{topic.description}</span>
                    ) : (
                      <span className="ra-topic-desc-missing">
                        <FaExclamationTriangle /> {t('routerAgent.descriptionMissing', 'À compléter — le routeur choisit le sujet d’après sa description.')}
                      </span>
                    )}
                  </div>
                  {typeof volume === 'number' && (
                    <div className="ra-topic-volume" title={t('routerAgent.topicVolumeTitle', '{{total}} tickets routés · {{period}}', { total: volume, period: periodLabel })}>
                      <span className="ra-topic-volume-count">{volume.toLocaleString(i18n.language || 'fr')}</span>
                      <span className="ra-topic-volume-bar">
                        <span className="ra-topic-volume-fill" style={{ width: `${(volume / maxTopicVolume) * 100}%` }} />
                      </span>
                    </div>
                  )}
                  <div className="ra-row-actions">
                    <button type="button" className="ra-icon-btn" onClick={() => openTopicModal(topic)} title={t('routerAgent.editTopic', 'Modifier le sujet')} aria-label={t('routerAgent.editTopic', 'Modifier le sujet')}>
                      <FaPen />
                    </button>
                    <button
                      type="button"
                      className="ra-icon-btn danger"
                      onClick={() => deleteTopic(topic)}
                      disabled={deletingIds.includes(topic._id)}
                      title={t('common.delete', 'Supprimer')}
                      aria-label={t('common.delete', 'Supprimer')}
                    >
                      {deletingIds.includes(topic._id) ? <FaSpinner className="ra-spin" /> : <FaTrash />}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {topics !== null && topics.length > 0 && topicVolumes && (
          <div className="ra-card-footnote">
            {t('routerAgent.topicVolumeNote', 'Volumes : tickets routés sur la période « {{period}} ».', { period: periodLabel })}
          </div>
        )}
      </div>
    </div>
  );

  const renderClassificationTab = () => (
    <div className="ra-card">
      <div className="ra-card-header">
        <div className="ra-card-heading">
          <h2>{t('routerAgent.classif.title', 'Classification des tickets')}</h2>
          <p>{t('routerAgent.classif.intro', 'Les sujets standards décrivent l’objet du ticket (suivi, marchandise endommagée, retour…). Axe indépendant du routage : activer la classification ne modifie jamais vos sujets de routage.')}</p>
        </div>
      </div>
      {topicsConfig === null ? skeleton(4) : (
        <>
          <div className="ra-classif-switch-row">
            <button
              type="button"
              role="switch"
              aria-checked={topicsConfig.classification_enabled}
              className={`ra-switch large ${topicsConfig.classification_enabled ? 'on' : ''}`}
              disabled={topicsConfigSaving}
              onClick={() => saveTopicsConfig({ ...topicsConfig, classification_enabled: !topicsConfig.classification_enabled })}
            >
              <span className="ra-switch-knob" />
            </button>
            <div>
              <div className="ra-classif-switch-label">{t('routerAgent.classif.toggle', 'Activer la classification automatique')}</div>
              <div className="ra-classif-switch-desc">{t('routerAgent.classif.toggleDesc', 'Chaque nouveau ticket entrant est étiqueté avec le sujet standard le plus adapté.')}</div>
            </div>
          </div>

          <div className="ra-classif-section">
            <h3>{t('routerAgent.classif.defaultTopics', 'Sujets standards')}</h3>
            <div className="ra-classif-chips">
              {(topicsConfig.default_topics || []).map((topic) => {
                const enabled = !(topicsConfig.disabled_topics || []).includes(topic.slug);
                return (
                  <button
                    key={topic.slug}
                    type="button"
                    className={`ra-classif-chip ${enabled ? 'on' : ''}`}
                    aria-pressed={enabled}
                    disabled={topicsConfigSaving}
                    onClick={() => {
                      const disabled = new Set(topicsConfig.disabled_topics || []);
                      if (disabled.has(topic.slug)) disabled.delete(topic.slug); else disabled.add(topic.slug);
                      saveTopicsConfig({ ...topicsConfig, disabled_topics: [...disabled] });
                    }}
                  >
                    {enabled && <FaCheck />}
                    {topic.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="ra-classif-section">
            <h3>
              {t('routerAgent.classif.customTopics', 'Sujets personnalisés')}
              {(topicsConfig.custom_topics || []).length > 0 && (
                <span className="ra-card-count">{topicsConfig.custom_topics.length}</span>
              )}
            </h3>
            {(topicsConfig.custom_topics || []).length === 0 ? (
              <p className="ra-muted-text">{t('routerAgent.classif.noCustom', 'Aucun sujet personnalisé pour le moment.')}</p>
            ) : (
              <ul className="ra-custom-list">
                {(topicsConfig.custom_topics || []).map((topic) => (
                  <li key={topic.slug} className="ra-custom-row">
                    <span className="ra-custom-name">{topic.name}</span>
                    {topic.description && <span className="ra-custom-desc" title={topic.description}>{topic.description}</span>}
                    <button
                      type="button"
                      className="ra-icon-btn danger"
                      disabled={topicsConfigSaving}
                      onClick={() => saveTopicsConfig({
                        ...topicsConfig,
                        custom_topics: (topicsConfig.custom_topics || []).filter((c) => c.slug !== topic.slug),
                      })}
                      title={t('common.delete', 'Supprimer')}
                      aria-label={t('common.delete', 'Supprimer')}
                    >
                      <FaTrash />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="ra-classif-add">
              <input
                type="text"
                placeholder={t('routerAgent.classif.customName', 'Nom du sujet')}
                value={customTopicForm.name}
                onChange={(e) => setCustomTopicForm((f) => ({ ...f, name: e.target.value }))}
              />
              <input
                type="text"
                placeholder={t('routerAgent.classif.customDescription', 'Description (aide l’IA à classifier)')}
                value={customTopicForm.description}
                onChange={(e) => setCustomTopicForm((f) => ({ ...f, description: e.target.value }))}
              />
              <button
                type="button"
                className="u-btn u-btn--secondary"
                disabled={topicsConfigSaving || !customTopicForm.name.trim()}
                onClick={() => {
                  saveTopicsConfig({
                    ...topicsConfig,
                    custom_topics: [
                      ...(topicsConfig.custom_topics || []),
                      { name: customTopicForm.name.trim(), description: customTopicForm.description.trim() },
                    ],
                  });
                  setCustomTopicForm({ name: '', description: '' });
                }}
              >
                <FaPlus /> {t('routerAgent.classif.addCustom', 'Ajouter')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderJournalTab = () => {
    const spamCount = stats?.spam_count || 0;
    const total = stats?.total_routings || 0;
    const routedCount = Math.max(0, total - spamCount);
    return (
      <div className="ra-stack">
        {/* One toolbar drives stats AND logs */}
        <div className="ra-journal-toolbar">
          <label className="ra-toolbar-field">
            <span>{t('routerAgent.logs.period', 'Période')}</span>
            <select value={statsDays} onChange={(e) => setStatsDays(Number(e.target.value))}>
              <option value={7}>{t('routerAgent.logs.last7', '7 derniers jours')}</option>
              <option value={30}>{t('routerAgent.logs.last30', '30 derniers jours')}</option>
              <option value={90}>{t('routerAgent.logs.last90', '90 derniers jours')}</option>
              <option value={365}>{t('routerAgent.logs.lastYear', '12 derniers mois')}</option>
            </select>
          </label>
          {brands.length > 0 && (
            <label className="ra-toolbar-field">
              <span>{t('routerAgent.logs.brand', 'Marque')}</span>
              <select value={journalBrand} onChange={(e) => { setJournalBrand(e.target.value); setLogsPage(1); }}>
                <option value="">{t('routerAgent.allBrands', 'Toutes les marques')}</option>
                {brands.map((b) => <option key={brandId(b)} value={brandId(b)}>{b.name}</option>)}
              </select>
            </label>
          )}
          <label className="ra-toolbar-field">
            <span>{t('routerAgent.logs.topicCol', 'Sujet')}</span>
            <select value={logFilters.topic} onChange={(e) => { setLogFilters((f) => ({ ...f, topic: e.target.value })); setLogsPage(1); }}>
              <option value="">{t('routerAgent.logs.all', 'Tous')}</option>
              {topicFilterOptions.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
            </select>
          </label>
          <label className="ra-toolbar-field">
            <span>{t('routerAgent.logs.decision', 'Décision')}</span>
            <select value={logFilters.is_spam} onChange={(e) => { setLogFilters((f) => ({ ...f, is_spam: e.target.value })); setLogsPage(1); }}>
              <option value="">{t('routerAgent.logs.all', 'Tous')}</option>
              <option value="false">{t('routerAgent.logs.notSpam', 'Non spam')}</option>
              <option value="true">{t('routerAgent.logs.spamOnly', 'Spam uniquement')}</option>
            </select>
          </label>
          <label className="ra-toolbar-field">
            <span>{t('routerAgent.logs.source', 'Source')}</span>
            <select value={logFilters.source} onChange={(e) => { setLogFilters((f) => ({ ...f, source: e.target.value })); setLogsPage(1); }}>
              <option value="">{t('routerAgent.logs.all', 'Toutes')}</option>
              <option value="outlook">Outlook</option>
              <option value="web_form">{t('routerAgent.logs.webForm', 'Formulaire web')}</option>
            </select>
          </label>
          <div className="ra-toolbar-actions">
            <button type="button" className="ra-icon-btn" onClick={() => { fetchStats(); fetchLogs(); }} title={t('routerAgent.logs.refresh', 'Actualiser')} aria-label={t('routerAgent.logs.refresh', 'Actualiser')}>
              <FaSync />
            </button>
            <button type="button" className="u-btn u-btn--secondary" onClick={handleExportCSV}>
              <FaDownload /> {t('routerAgent.logs.exportCSV', 'Export CSV')}
            </button>
          </div>
        </div>

        {/* Stats — hero + composition + breakdowns */}
        {stats && total > 0 && (
          <div className="ra-card ra-stats-card">
            <div className="ra-stats-hero">
              <div className="ra-stats-hero-main">
                <span className="ra-stats-hero-value">{total.toLocaleString(i18n.language || 'fr')}</span>
                <span className="ra-stats-hero-label">
                  {t('routerAgent.logs.totalRoutings', 'décisions de routage')} · {periodLabel}
                  {journalBrand && ` · ${getBrandName(journalBrand)}`}
                </span>
              </div>
              <div className="ra-stats-composition">
                <div className="ra-comp-bar" role="img" aria-label={t('routerAgent.logs.compositionAria', 'Répartition routés / spam')}>
                  <span className="ra-comp-seg routed" style={{ width: `${(routedCount / total) * 100}%` }} />
                  <span className="ra-comp-seg spam" style={{ width: `${(spamCount / total) * 100}%` }} />
                </div>
                <div className="ra-comp-legend">
                  <span className="ra-comp-item routed">
                    {t('routerAgent.logs.routedCount', '{{total}} routés', { total: routedCount.toLocaleString(i18n.language || 'fr') })}
                  </span>
                  <span className="ra-comp-item spam">
                    {t('routerAgent.logs.spamCountLabel', '{{total}} spam ({{rate}} %)', {
                      total: spamCount.toLocaleString(i18n.language || 'fr'),
                      rate: (stats.spam_rate * 100).toFixed(1),
                    })}
                  </span>
                </div>
              </div>
            </div>
            {(Object.keys(stats.topic_breakdown || {}).length > 0 || Object.keys(stats.assistant_breakdown || {}).length > 0) && (
              <div className="ra-breakdowns">
                {Object.keys(stats.topic_breakdown || {}).length > 0 && (
                  <div className="ra-breakdown">
                    <h3>{t('routerAgent.logs.topicBreakdown', 'Par sujet')}</h3>
                    {Object.entries(stats.topic_breakdown).sort((a, b) => b[1] - a[1]).map(([topic, count]) => (
                      <div key={topic} className="ra-breakdown-row">
                        <span className="ra-breakdown-label" title={topic}>{topic}</span>
                        <span className="ra-breakdown-count">{count.toLocaleString(i18n.language || 'fr')}</span>
                        <span className="ra-breakdown-bar">
                          <span className="ra-breakdown-fill" style={{ width: `${(count / total) * 100}%` }} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {Object.keys(stats.assistant_breakdown || {}).length > 0 && (
                  <div className="ra-breakdown">
                    <h3>{t('routerAgent.logs.assistantBreakdown', 'Par agent IA')}</h3>
                    {Object.entries(stats.assistant_breakdown).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                      <div key={name} className="ra-breakdown-row">
                        <span className="ra-breakdown-label" title={name}>{name}</span>
                        <span className="ra-breakdown-count">{count.toLocaleString(i18n.language || 'fr')}</span>
                        <span className="ra-breakdown-bar">
                          <span className="ra-breakdown-fill assistant" style={{ width: `${(count / total) * 100}%` }} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Decisions table */}
        <div className="ra-card">
          <div className="ra-card-header">
            <div className="ra-card-heading">
              <h2>
                {t('routerAgent.logs.logsTitle', 'Décisions')}
                {logs !== null && <span className="ra-card-count">{logsTotal.toLocaleString(i18n.language || 'fr')}</span>}
              </h2>
            </div>
          </div>
          {logs === null ? skeleton(6) : logs.length === 0 ? (
            <div className="ra-empty">
              <p>{t('routerAgent.logs.noLogs', 'Aucune décision de routage ne correspond aux filtres.')}</p>
            </div>
          ) : (
            <>
              <div className="ra-table-scroll">
                <table className="ra-table">
                  <colgroup>
                    <col style={{ width: '170px' }} />
                    <col style={{ width: '110px' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '24%' }} />
                    <col style={{ width: '120px' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t('routerAgent.logs.date', 'Date')}</th>
                      <th>{t('routerAgent.logs.source', 'Source')}</th>
                      <th>{t('routerAgent.logs.topicCol', 'Sujet')}</th>
                      <th>{t('routerAgent.logs.assistant', 'Agent')}</th>
                      <th>{t('routerAgent.logs.decision', 'Décision')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => {
                      const decision = decisionOf(log);
                      return (
                        <tr
                          key={log.id}
                          className={`ra-log-row ${selectedLog?.id === log.id ? 'selected' : ''}`}
                          tabIndex={0}
                          onClick={() => { setSelectedLog(log); setShowLogIds(false); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedLog(log); setShowLogIds(false); } }}
                        >
                          <td>{formatDate(log.created_at) || '—'}</td>
                          <td>{log.source === 'web_form' ? t('routerAgent.logs.webForm', 'Formulaire web') : (log.source || '—')}</td>
                          <td title={log.topic || ''}>{log.topic || <span className="ra-muted-dash">—</span>}</td>
                          <td title={log.assistant_name || ''}>{log.assistant_name || <span className="ra-muted-dash">—</span>}</td>
                          <td>
                            <span className={`ra-decision ${decision.key}`}>
                              <span className="ra-decision-dot" aria-hidden="true" />
                              {decision.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalLogsPages > 1 && (
                <div className="ra-card-footer">
                  <nav className="ra-pager" aria-label={t('routerAgent.logs.pagination', 'Pagination')}>
                    <button type="button" disabled={logsPage === 1} onClick={() => setLogsPage((p) => p - 1)} aria-label={t('common.previous')}>‹</button>
                    {getPageItems().map((item) => (
                      typeof item === 'number' ? (
                        <button
                          key={item}
                          type="button"
                          className={item === logsPage ? 'active' : ''}
                          aria-current={item === logsPage ? 'page' : undefined}
                          onClick={() => setLogsPage(item)}
                        >
                          {item}
                        </button>
                      ) : (
                        <span key={item} className="ra-pager-ellipsis" aria-hidden="true">…</span>
                      )
                    ))}
                    <button type="button" disabled={logsPage === totalLogsPages} onClick={() => setLogsPage((p) => p + 1)} aria-label={t('common.next')}>›</button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ══════════ Render ══════════

  return (
    <div className="router-agent-page">
      {/* Header */}
      <div className="ra-header">
        <div className="ra-header-left">
          <h1>{t('routerAgent.title', 'Agent Routeur')}</h1>
          <p className="ra-header-subtitle">
            {t('routerAgent.subtitle', 'Trie chaque ticket entrant : spam, sujet de routage, agent IA.')}
          </p>
        </div>
      </div>

      <div className="ra-tabs" role="tablist" aria-label={t('routerAgent.title', 'Agent Routeur')}>
        <button type="button" role="tab" aria-selected={activeTab === 'routing'} className={`ra-tab ${activeTab === 'routing' ? 'active' : ''}`} onClick={() => setActiveTab('routing')}>
          <FaProjectDiagram aria-hidden="true" /> {t('routerAgent.routingTab', 'Routage')}
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'classification'} className={`ra-tab ${activeTab === 'classification' ? 'active' : ''}`} onClick={() => setActiveTab('classification')}>
          <FaCommentDots aria-hidden="true" /> {t('routerAgent.classificationTab', 'Classification')}
        </button>
        <button type="button" role="tab" aria-selected={activeTab === 'journal'} className={`ra-tab ${activeTab === 'journal' ? 'active' : ''}`} onClick={() => setActiveTab('journal')}>
          <FaListAlt aria-hidden="true" /> {t('routerAgent.journalTab', 'Journal')}
        </button>
      </div>

      {feedback && (
        <div className={`ra-feedback ${feedback.type}`} role="status">
          {feedback.type === 'success' ? <FaCheckCircle /> : <FaTimesCircle />}
          {feedback.text}
        </div>
      )}

      {activeTab === 'routing' && renderRoutingTab()}
      {activeTab === 'classification' && renderClassificationTab()}
      {activeTab === 'journal' && renderJournalTab()}

      {/* Log detail drawer */}
      {selectedLog && (
        <>
          <div className="ra-drawer-backdrop" onClick={() => setSelectedLog(null)} />
          <aside className="ra-drawer" role="dialog" aria-modal="true">
            <div className="ra-drawer-header">
              <h3>{t('routerAgent.logs.logDetail', 'Décision du routeur')}</h3>
              <button type="button" className="ra-icon-btn" onClick={() => setSelectedLog(null)} aria-label={t('common.cancel', 'Fermer')}>
                <FaTimes />
              </button>
            </div>
            <div className="ra-drawer-body">
              <div className="ra-drawer-decision">
                <span className={`ra-decision large ${decisionOf(selectedLog).key}`}>
                  <span className="ra-decision-dot" aria-hidden="true" />
                  {decisionOf(selectedLog).label}
                </span>
                {selectedLog.topic && <span className="ra-drawer-topic">{selectedLog.topic}</span>}
              </div>

              {selectedLog.redirected_to && (
                <div className="ra-drawer-redirect">
                  <FaShareSquare />
                  <a href={`mailto:${selectedLog.redirected_to}`}>{selectedLog.redirected_to}</a>
                </div>
              )}

              <div className="ra-drawer-meta">
                <div>
                  <span className="ra-drawer-meta-label">{t('routerAgent.logs.date', 'Date')}</span>
                  <span>{formatDate(selectedLog.created_at) || '—'}</span>
                </div>
                <div>
                  <span className="ra-drawer-meta-label">{t('routerAgent.logs.source', 'Source')}</span>
                  <span>{selectedLog.source === 'web_form' ? t('routerAgent.logs.webForm', 'Formulaire web') : (selectedLog.source || '—')}</span>
                </div>
                {selectedLog.assistant_name && (
                  <div>
                    <span className="ra-drawer-meta-label">{t('routerAgent.logs.assistant', 'Agent')}</span>
                    <span>{selectedLog.assistant_name}</span>
                  </div>
                )}
                {selectedLog.brand_id && (
                  <div>
                    <span className="ra-drawer-meta-label">{t('routerAgent.logs.brand', 'Marque')}</span>
                    <span>{getBrandName(selectedLog.brand_id)}</span>
                  </div>
                )}
              </div>

              {selectedLog.reason && (
                <div className="ra-drawer-section">
                  <h4>{t('routerAgent.logs.reason', 'Raison')}</h4>
                  <div className="ra-drawer-text">{selectedLog.reason}</div>
                </div>
              )}

              {selectedLog.customer_message_snippet && (
                <div className="ra-drawer-section">
                  <h4>{t('routerAgent.logs.customerMsg', 'Message client')}</h4>
                  <div className="ra-drawer-text">{selectedLog.customer_message_snippet}</div>
                </div>
              )}

              {selectedLog.ticket_id && (
                <button
                  type="button"
                  className="u-btn u-btn--primary ra-drawer-ticket"
                  onClick={() => navigate(`/tickets/${selectedLog.ticket_id}/messages`)}
                >
                  {t('routerAgent.logs.goToTicket', 'Ouvrir le ticket')} <FaExternalLinkAlt />
                </button>
              )}

              <button type="button" className="ra-drawer-ids-toggle" onClick={() => setShowLogIds((v) => !v)}>
                {showLogIds ? <FaChevronDown /> : <FaChevronRight />}
                {t('routerAgent.logs.technicalIds', 'Identifiants techniques')}
              </button>
              {showLogIds && (
                <div className="ra-drawer-ids">
                  <div><span>{t('routerAgent.logs.ticketId', 'Ticket')}</span><code>{selectedLog.ticket_id || '—'}</code></div>
                  {selectedLog.assistant_id && (
                    <div><span>{t('routerAgent.logs.assistantId', 'Agent')}</span><code>{selectedLog.assistant_id}</code></div>
                  )}
                  <div><span>{t('routerAgent.logs.logId', 'Log')}</span><code>{selectedLog.id}</code></div>
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      {/* Topic modal */}
      {topicModal && (
        <div className="ra-modal-backdrop" onClick={() => setTopicModal(null)}>
          <div className="ra-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="ra-modal-header">
              <h3>{topicModal.editing ? t('routerAgent.editTopic', 'Modifier le sujet') : t('routerAgent.createTopic', 'Créer un sujet')}</h3>
              <button type="button" className="ra-icon-btn" onClick={() => setTopicModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                <FaTimes />
              </button>
            </div>
            <div className="ra-modal-body">
              <div className="ra-field">
                <label htmlFor="ra-topic-name">{t('routerAgent.topicName', 'Nom du sujet')} *</label>
                <input
                  id="ra-topic-name"
                  type="text"
                  value={topicModal.form.name}
                  onChange={(e) => setTopicModal((m) => ({ ...m, form: { ...m.form, name: e.target.value } }))}
                  placeholder={t('routerAgent.namePlaceholder', 'Ex. : Avant vente, Après vente, Facturation…')}
                  autoFocus
                />
              </div>
              <div className="ra-field">
                <label htmlFor="ra-topic-desc">{t('routerAgent.description', 'Description')}</label>
                <p className="ra-field-hint">
                  {t('routerAgent.descriptionHint', 'C’est cette description que le routeur lit pour décider quels tickets classer ici — soyez précis.')}
                </p>
                <textarea
                  id="ra-topic-desc"
                  rows={5}
                  maxLength={2000}
                  value={topicModal.form.description}
                  onChange={(e) => setTopicModal((m) => ({ ...m, form: { ...m.form, description: e.target.value } }))}
                  placeholder={t('routerAgent.descriptionPlaceholder', 'Ex. : Questions posées avant l’achat — devis, disponibilité, délais de livraison…')}
                />
              </div>
              {topicModal.error && <div className="ra-modal-error">{topicModal.error}</div>}
            </div>
            <div className="ra-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setTopicModal(null)} disabled={topicModal.saving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={saveTopic} disabled={topicModal.saving}>
                {topicModal.saving
                  ? (<><FaSpinner className="ra-spin" /> {t('routerAgent.saving', 'Enregistrement…')}</>)
                  : t('common.save', 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instruction modal */}
      {instrModal && (
        <div className="ra-modal-backdrop" onClick={() => setInstrModal(null)}>
          <div className="ra-modal ra-modal-wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="ra-modal-header">
              <h3>
                {instrModal.editing
                  ? t('routerAgent.instr.edit', 'Modifier les instructions')
                  : t('routerAgent.instr.create', 'Créer des instructions')}
              </h3>
              <button type="button" className="ra-icon-btn" onClick={() => setInstrModal(null)} aria-label={t('common.cancel', 'Annuler')}>
                <FaTimes />
              </button>
            </div>
            <div className="ra-modal-body">
              <div className="ra-form-section">
                <h4>{t('routerAgent.instr.sectionScope', 'Portée')}</h4>
                {instrModal.editing ? (
                  <p className="ra-field-hint">
                    {t('routerAgent.instr.scopeLocked', 'Portée : {{scope}} (non modifiable après création).', { scope: getBrandName(instrModal.editing.brand_id) })}
                  </p>
                ) : (
                  <div className="ra-field">
                    <label htmlFor="ra-instr-brand">{t('routerAgent.instr.brand', 'Marque')}</label>
                    <select
                      id="ra-instr-brand"
                      value={instrModal.form.brand_id}
                      onChange={(e) => setInstrModal((m) => ({ ...m, form: { ...m.form, brand_id: e.target.value } }))}
                    >
                      <option value="">{t('routerAgent.allBrands', 'Toutes les marques')}</option>
                      {brands.map((b) => <option key={brandId(b)} value={brandId(b)}>{b.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="ra-field">
                  <label>{t('routerAgent.instr.applyToEmails', 'Boîtes mail concernées')}</label>
                  <p className="ra-field-hint">
                    {t('routerAgent.instr.applyToEmailsHelp', 'Restreindre à certaines boîtes Outlook connectées. Vide = tout le courrier entrant de la portée.')}
                  </p>
                  {outlookAccounts.length > 0 ? (
                    <div className="ra-mailbox-chips">
                      {outlookAccounts.map((acct) => {
                        const email = acct.outlook_email || '';
                        if (!email) return null;
                        const checked = instrModal.form.apply_to_emails.includes(email);
                        return (
                          <button
                            key={email}
                            type="button"
                            className={`ra-classif-chip ${checked ? 'on' : ''}`}
                            aria-pressed={checked}
                            onClick={() => setInstrModal((m) => ({
                              ...m,
                              form: {
                                ...m.form,
                                apply_to_emails: checked
                                  ? m.form.apply_to_emails.filter((em) => em !== email)
                                  : [...m.form.apply_to_emails, email],
                              },
                            }))}
                          >
                            {checked && <FaCheck />}
                            {email}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="ra-muted-text">{t('routerAgent.instr.noOutlookAccounts', 'Aucune boîte Outlook connectée.')}</p>
                  )}
                </div>
              </div>

              <div className="ra-form-section">
                <h4>{t('routerAgent.instr.sectionBehavior', 'Instructions de routage')}</h4>
                <textarea
                  rows={8}
                  maxLength={100000}
                  value={instrModal.form.instructions}
                  onChange={(e) => setInstrModal((m) => ({ ...m, form: { ...m.form, instructions: e.target.value } }))}
                  placeholder={t('routerAgent.instr.instructionsPlaceholder', 'Décrivez comment le routeur doit classer et router les tickets entrants…')}
                />
              </div>

              <div className="ra-form-section">
                <h4>{t('routerAgent.instr.sectionRedirect', 'Redirections')}</h4>
                <p className="ra-field-hint">
                  {t('routerAgent.instr.redirectEmailsHelp', 'Adresses vers lesquelles le routeur peut transférer un courriel (pas besoin d’être des comptes connectés).')}
                </p>
                <div className="ra-tag-input">
                  {instrModal.form.redirect_emails.map((email) => (
                    <span key={email} className="ra-tag">
                      {email}
                      <button
                        type="button"
                        onClick={() => setInstrModal((m) => ({
                          ...m,
                          form: { ...m.form, redirect_emails: m.form.redirect_emails.filter((e2) => e2 !== email) },
                        }))}
                        aria-label={t('common.delete', 'Supprimer')}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                  <input
                    type="email"
                    value={instrModal.redirectInput}
                    onChange={(e) => setInstrModal((m) => ({ ...m, redirectInput: e.target.value }))}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ',') && instrModal.redirectInput.trim()) {
                        e.preventDefault();
                        const val = instrModal.redirectInput.trim().replace(/,$/, '');
                        setInstrModal((m) => ({
                          ...m,
                          redirectInput: '',
                          form: {
                            ...m.form,
                            redirect_emails: m.form.redirect_emails.includes(val)
                              ? m.form.redirect_emails
                              : [...m.form.redirect_emails, val],
                          },
                        }));
                      }
                    }}
                    placeholder={t('routerAgent.instr.redirectPlaceholder', 'Saisir une adresse puis Entrée')}
                  />
                </div>
              </div>

              <div className="ra-form-section">
                <h4>{t('routerAgent.instr.sectionActivation', 'Activation')}</h4>
                <div className="ra-activation-row">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={instrModal.form.is_active}
                    className={`ra-switch large ${instrModal.form.is_active ? 'on' : ''}`}
                    onClick={() => setInstrModal((m) => ({ ...m, form: { ...m.form, is_active: !m.form.is_active } }))}
                  >
                    <span className="ra-switch-knob" />
                  </button>
                  <span className="ra-activation-label">
                    {instrModal.form.is_active
                      ? t('routerAgent.instr.activeLabel', 'Actives — le routage traite cette portée')
                      : t('routerAgent.instr.inactiveLabel', 'Désactivées — les tickets de cette portée ne seront pas routés')}
                  </span>
                </div>
              </div>

              {instrModal.error && <div className="ra-modal-error">{instrModal.error}</div>}
            </div>
            <div className="ra-modal-footer">
              <button type="button" className="u-btn u-btn--secondary" onClick={() => setInstrModal(null)} disabled={instrModal.saving}>
                {t('common.cancel', 'Annuler')}
              </button>
              <button type="button" className="u-btn u-btn--primary" onClick={saveInstruction} disabled={instrModal.saving}>
                {instrModal.saving
                  ? (<><FaSpinner className="ra-spin" /> {t('routerAgent.saving', 'Enregistrement…')}</>)
                  : t('common.save', 'Enregistrer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouterAgent;
